// // context/AuthContext.tsx
// 'use client';

// import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
// import { useRouter } from 'next/navigation';
// import { api } from '@/lib/api';
// import { User, LoginResponse } from '@/types/auth';

// interface AuthContextType {
//   user: User | null;
//   loading: boolean;
//   login: (email: string, password: string) => Promise<LoginResponse>;
//   logout: () => Promise<void>;
//   refreshAccessToken: () => Promise<string | null>;
//   updateUser: (userData: User) => void;
//   isAuthenticated: boolean;
//   hasRole: (role: User['role']) => boolean;
// }

// const AuthContext = createContext<AuthContextType | undefined>(undefined);

// interface AuthProviderProps {
//   children: ReactNode;
// }

// export const useAuth = (): AuthContextType => {
//   const context = useContext(AuthContext);
//   if (!context) {
//     throw new Error('useAuth must be used within AuthProvider');
//   }
//   return context;
// };

// export const AuthProvider = ({ children }: AuthProviderProps) => {
//   const [user, setUser] = useState<User | null>(null);
//   const [loading, setLoading] = useState(true);
//   const [tokenRefreshInterval, setTokenRefreshInterval] = useState<NodeJS.Timeout | null>(null);
//   const router = useRouter();

//   // Initialize auth from localStorage
//   useEffect(() => {
//     const initAuth = async () => {
//       const accessToken = localStorage.getItem('accessToken');
//       const refreshToken = localStorage.getItem('refreshToken');
      
//       if (accessToken && refreshToken) {
//         try {
//           const session = await api.getSession();
//           setUser(session.data.user);
//           startTokenRefresh();
//         } catch (error) {
//           // Token might be expired, try to refresh
//           try {
//             await refreshAccessToken();
//           } catch (refreshError) {
//             logout();
//           }
//         }
//       }
//       setLoading(false);
//     };

//     initAuth();
//   }, []);

//   // Start token refresh interval
//   const startTokenRefresh = () => {
//     // Clear existing interval
//     if (tokenRefreshInterval) {
//       clearInterval(tokenRefreshInterval);
//     }

//     // Refresh token every 14 minutes (tokens expire in 15 minutes)
//     const interval = setInterval(() => {
//       refreshAccessToken();
//     }, 14 * 60 * 1000);

//     setTokenRefreshInterval(interval);
//   };

//   // Refresh access token
//   const refreshAccessToken = async (): Promise<string | null> => {
//     try {
//       const refreshToken = localStorage.getItem('refreshToken');
//       if (!refreshToken) {
//         throw new Error('No refresh token');
//       }

//       const result = await api.refreshToken(refreshToken);
      
//       localStorage.setItem('accessToken', result.data.accessToken);
//       localStorage.setItem('tokenExpiry', result.data.expiresAt);
      
//       return result.data.accessToken;
//     } catch (error) {
//       logout();
//       return null;
//     }
//   };

//   // Login function
//   const login = async (email: string, password: string): Promise<LoginResponse> => {
//     setLoading(true);
//     try {
//       const result = await api.login(email, password);
      
//       // Store tokens
//       localStorage.setItem('accessToken', result.data.tokens.accessToken);
//       localStorage.setItem('refreshToken', result.data.tokens.refreshToken);
//       localStorage.setItem('tokenExpiry', result.data.tokens.expiresAt);
//       localStorage.setItem('user', JSON.stringify(result.data.user));
      
//       setUser(result.data.user);
//       startTokenRefresh();
      
//       return result;
//     } catch (error) {
//       throw error;
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Logout function
//   const logout = async (): Promise<void> => {
//     try {
//       await api.logout();
//     } catch (error) {
//       console.error('Logout error:', error);
//     } finally {
//       // Clear all auth data
//       localStorage.removeItem('accessToken');
//       localStorage.removeItem('refreshToken');
//       localStorage.removeItem('tokenExpiry');
//       localStorage.removeItem('user');
      
//       // Clear interval
//       if (tokenRefreshInterval) {
//         clearInterval(tokenRefreshInterval);
//         setTokenRefreshInterval(null);
//       }
      
//       setUser(null);
//       router.push('/');
//     }
//   };

//   // Update user profile
//   const updateUser = (userData: User): void => {
//     setUser(userData);
//     localStorage.setItem('user', JSON.stringify(userData));
//   };

//   // Check authentication status
//   const isAuthenticated = !!user;

//   // Check user role
//   const hasRole = (role: User['role']): boolean => {
//     return user?.role === role;
//   };

//   const value: AuthContextType = {
//     user,
//     loading,
//     login,
//     logout,
//     refreshAccessToken,
//     updateUser,
//     isAuthenticated,
//     hasRole,
//   };

//   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
// };

// context/AuthContext.tsx
'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { api, ApiError } from '@/lib/api';
import { User, LoginResponse } from '@/types/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<string | null>;
  updateUser: (userData: User) => void;
  isAuthenticated: boolean;
  hasRole: (role: User['role']) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const getRedirectPathByRole = (role?: string | null) => {
  const normalizedRole = String(role || "")
    .toLowerCase()
    .replace(/_/g, "-");

  if (normalizedRole === "director-tpv") {
    return "/director-dashboard";
  }

  return "/";
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenRefreshInterval, setTokenRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const router = useRouter();

  // Initialize auth from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (accessToken && refreshToken) {
        try {
          const session = await api.getSession();
          setUser(session.data.user);
          startTokenRefresh();
        } catch (error: any) {
          // Token might be expired, try to refresh
          try {
            await refreshAccessToken();
          } catch (refreshError) {
            // Show error toast and logout
            toast.error('Session expired. Please login again.');
            logout();
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Start token refresh interval
  const startTokenRefresh = () => {
    // Clear existing interval
    if (tokenRefreshInterval) {
      clearInterval(tokenRefreshInterval);
    }

    // Refresh token every 14 minutes (tokens expire in 15 minutes)
    const interval = setInterval(() => {
      refreshAccessToken();
    }, 14 * 60 * 1000);

    setTokenRefreshInterval(interval);
  };

  // Refresh access token
  const refreshAccessToken = async (): Promise<string | null> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token');
      }

      const result = await api.refreshToken(refreshToken);
      
      localStorage.setItem('accessToken', result.data.accessToken);
      localStorage.setItem('tokenExpiry', result.data.expiresAt);
      
      return result.data.accessToken;
    } catch (error: any) {
      throw error;
    }
  };

  // Login function
// context/AuthContext.tsx - Update the login function
const login = async (email: string, password: string): Promise<LoginResponse> => {
  setLoading(true);
  console.log('🔍 AuthContext: Starting login process');
  
  try {
    console.log('🔍 Calling API login...');
    
    // Get the FULL response from API
    const response = await api.login(email, password) as LoginResponse;
    
    console.log('✅ AuthContext: API login response received');
    console.log('Full response:', response);
    console.log('Response success:', response.success);
    console.log('Response data:', response.data);
    
    if (!response.success || !response.data) {
      console.error('❌ Login response indicates failure');
      throw new ApiError(
        response.error?.message || 'Login failed',
        response.error?.code || 'LOGIN_FAILED'
      );
    }
    
    if (!response.data.tokens || !response.data.user) {
      console.error('❌ Missing tokens or user data in response');
      console.error('Tokens:', response.data.tokens);
      console.error('User:', response.data.user);
      throw new ApiError('Invalid response format from server', 'INVALID_RESPONSE');
    }
    
    console.log('✅ Valid response structure received');
    
    // Store tokens
    localStorage.setItem('accessToken', response.data.tokens.accessToken);
    localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
    localStorage.setItem('tokenExpiry', response.data.tokens.expiresAt);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    
    console.log('🔍 Tokens stored in localStorage');
    console.log('User stored:', response.data.user.email);
    
    setUser(response.data.user);
    console.log('✅ User state updated in AuthContext');
    
    startTokenRefresh();
    
    // Show success toast
    toast.success(response.message || 'Login successful!');

    // Redirect user based on role
    const redirectPath = getRedirectPathByRole(response.data.user.role);
    router.replace(redirectPath);
    
    // Return the full response
    return response;
    
  } catch (error: any) {
    console.error('❌ AuthContext: Login error', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    
    // Show error toast
    const errorMessage = error.message || 'Login failed. Please try again.';
    toast.error(errorMessage);
    
    throw error;
  } finally {
    setLoading(false);
    console.log('🔍 AuthContext: Login process completed');
  }
};

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      await api.logout();
      toast.success('Logged out successfully');
    } catch (error: any) {
      console.error('Logout error:', error);
      // Still show success toast even if API call fails
      toast.success('Logged out successfully');
    } finally {
      // Clear all auth data
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('user');
      
      // Clear interval
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        setTokenRefreshInterval(null);
      }
      
      setUser(null);
      router.push('/');
    }
  };

  // Update user profile
  const updateUser = (userData: User): void => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Check authentication status
  const isAuthenticated = !!user;

  // Check user role
  const hasRole = (role: User['role']): boolean => {
    return user?.role === role;
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    refreshAccessToken,
    updateUser,
    isAuthenticated,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};